/**
 * Commerce API Client
 * Handles all product commerce operations against the Route Royal Storefront API.
 * Uses the BFF proxy pattern (like CustomerAPIClient) for client-side calls.
 */

import { BaseAPIClient } from '../base-client';

// =============================================================================
// TYPES
// =============================================================================

export interface StorefrontProduct {
  id: string;
  title: string;
  handle: string;
  subtitle?: string;
  description?: string;
  descriptionHtml?: string;
  status: string;
  vendor?: string;
  brand?: string;
  currency: string;
  minPrice?: number;
  maxPrice?: number;
  averageRating?: number;
  reviewCount: number;
  thumbnailUrl?: string;
  seoTitle?: string;
  seoDescription?: string;
  isGiftCard?: boolean;
  isDigital?: boolean;
  variants: StorefrontVariant[];
  options?: StorefrontOption[];
  tags?: Array<{ id: string; name: string }>;
  categories?: Array<{ id: string; name: string; handle: string }>;
  metadata?: Record<string, unknown>;
}

export interface StorefrontVariant {
  id: string;
  title: string;
  sku?: string;
  price: number;
  compareAtPrice?: number;
  currency: string;
  imageUrl?: string;
  requiresShipping: boolean;
  available: boolean;
  optionValues?: Array<{ optionId: string; value: string }>;
}

export interface StorefrontOption {
  id: string;
  name: string;
  position: number;
  values: Array<{ id: string; value: string }>;
}

export interface StorefrontCollection {
  id: string;
  title: string;
  handle: string;
  description?: string;
  imageUrl?: string;
  productCount: number;
}

export interface StorefrontCategory {
  id: string;
  name: string;
  handle: string;
  description?: string;
  imageUrl?: string;
  children?: StorefrontCategory[];
  productCount: number;
}

export interface StorefrontCart {
  id: string;
  status: string;
  currency: string;
  subtotal: number;
  taxTotal: number;
  discountAmount: number;
  discountCode?: string;
  total: number;
  lineItems: StorefrontCartLineItem[];
}

export interface StorefrontCartLineItem {
  id: string;
  variantId: string;
  quantity: number;
  unitPrice: number;
  variantTitle?: string;
  productTitle?: string;
  sku?: string;
  imageUrl?: string;
}

export interface StorefrontReview {
  id: string;
  rating: number;
  title?: string;
  body?: string;
  authorName: string;
  verifiedPurchase: boolean;
  createdAt: string;
}

export interface CustomerOrder {
  id: string;
  orderNumber: string;
  status: string;
  currency: string;
  subtotal: number;
  taxTotal: number;
  discountAmount: number;
  total: number;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductListParams {
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
  categoryId?: string;
  collectionId?: string;
  minPrice?: number;
  maxPrice?: number;
  locale?: string;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// =============================================================================
// CLIENT
// =============================================================================

export class CommerceAPIClient extends BaseAPIClient {
  private organisationId: string;
  private accessToken: string | null = null;

  constructor(organisationId: string, baseUrl?: string) {
    super(baseUrl);
    this.organisationId = organisationId;
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  private isClientSide(): boolean {
    return typeof window !== 'undefined';
  }

  private async storefrontRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    if (this.isClientSide()) {
      return this.proxyRequest<T>(endpoint, options);
    }

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
      'X-Organisation-ID': this.organisationId,
    };

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    return this.request<T>(endpoint, { ...options, headers }, false);
  }

  private async proxyRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const method = (options.method || 'GET') as string;

    let body: Record<string, unknown> | undefined;
    if (typeof options.body === 'string') {
      body = JSON.parse(options.body) as Record<string, unknown>;
    }

    const response = await fetch('/api/commerce/proxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint,
        method,
        body,
        accessToken: this.accessToken,
        organisationId: this.organisationId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message =
        typeof errorData?.error === 'string'
          ? errorData.error
          : `Commerce API error: ${response.status}`;
      throw new Error(message);
    }

    const json = await response.json();
    if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
      return json.data as T;
    }
    return json as T;
  }

  // ===========================================================================
  // Products
  // ===========================================================================

  async getProducts(
    channelId: string,
    params: ProductListParams = {},
  ): Promise<PaginatedResult<StorefrontProduct>> {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));
    if (params.sort) search.set('sort', params.sort);
    if (params.search) search.set('search', params.search);
    if (params.categoryId) search.set('categoryId', params.categoryId);
    if (params.collectionId) search.set('collectionId', params.collectionId);
    if (params.minPrice) search.set('minPrice', String(params.minPrice));
    if (params.maxPrice) search.set('maxPrice', String(params.maxPrice));
    if (params.locale) search.set('locale', params.locale);
    const qs = search.toString();

    return this.storefrontRequest<PaginatedResult<StorefrontProduct>>(
      `/storefront/${channelId}/products${qs ? `?${qs}` : ''}`,
    );
  }

  async getProduct(
    channelId: string,
    handle: string,
    locale?: string,
  ): Promise<StorefrontProduct> {
    const qs = locale ? `?locale=${locale}` : '';
    return this.storefrontRequest<StorefrontProduct>(
      `/storefront/${channelId}/products/${handle}${qs}`,
    );
  }

  async searchProducts(
    channelId: string,
    query: string,
    locale?: string,
  ): Promise<PaginatedResult<StorefrontProduct>> {
    const params = new URLSearchParams({ search: query });
    if (locale) params.set('locale', locale);
    return this.storefrontRequest<PaginatedResult<StorefrontProduct>>(
      `/storefront/${channelId}/products?${params.toString()}`,
    );
  }

  // ===========================================================================
  // Collections
  // ===========================================================================

  async getCollections(
    channelId: string,
  ): Promise<StorefrontCollection[]> {
    return this.storefrontRequest<StorefrontCollection[]>(
      `/storefront/${channelId}/collections`,
    );
  }

  async getCollection(
    channelId: string,
    handle: string,
    locale?: string,
  ): Promise<StorefrontCollection & { products: StorefrontProduct[] }> {
    const qs = locale ? `?locale=${locale}` : '';
    return this.storefrontRequest(
      `/storefront/${channelId}/collections/${handle}${qs}`,
    );
  }

  // ===========================================================================
  // Categories
  // ===========================================================================

  async getCategories(
    channelId: string,
  ): Promise<StorefrontCategory[]> {
    return this.storefrontRequest<StorefrontCategory[]>(
      `/storefront/${channelId}/categories`,
    );
  }

  // ===========================================================================
  // Cart
  // ===========================================================================

  async getCart(cartId: string): Promise<StorefrontCart> {
    return this.storefrontRequest<StorefrontCart>(
      `/organisations/${this.organisationId}/cart/${cartId}`,
    );
  }

  async createCart(
    sessionId?: string,
    customerId?: string,
    currency?: string,
  ): Promise<StorefrontCart> {
    return this.storefrontRequest<StorefrontCart>(
      `/organisations/${this.organisationId}/cart`,
      {
        method: 'POST',
        body: JSON.stringify({ sessionId, customerId, currency }),
      },
    );
  }

  async addToCart(
    cartId: string,
    item: { variantId: string; quantity: number },
  ): Promise<StorefrontCart> {
    return this.storefrontRequest<StorefrontCart>(
      `/organisations/${this.organisationId}/cart/${cartId}/items`,
      {
        method: 'POST',
        body: JSON.stringify(item),
      },
    );
  }

  async updateCartItem(
    cartId: string,
    itemId: string,
    quantity: number,
  ): Promise<StorefrontCart> {
    return this.storefrontRequest<StorefrontCart>(
      `/organisations/${this.organisationId}/cart/${cartId}/items/${itemId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ quantity }),
      },
    );
  }

  async removeCartItem(
    cartId: string,
    itemId: string,
  ): Promise<StorefrontCart> {
    return this.storefrontRequest<StorefrontCart>(
      `/organisations/${this.organisationId}/cart/${cartId}/items/${itemId}`,
      { method: 'DELETE' },
    );
  }

  async applyDiscount(
    cartId: string,
    code: string,
  ): Promise<StorefrontCart> {
    return this.storefrontRequest<StorefrontCart>(
      `/organisations/${this.organisationId}/cart/${cartId}/apply-discount`,
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      },
    );
  }

  async checkout(
    cartId: string,
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ url: string; sessionId: string }> {
    const params = new URLSearchParams();
    if (successUrl) params.set('success_url', successUrl);
    if (cancelUrl) params.set('cancel_url', cancelUrl);
    const qs = params.toString();

    return this.storefrontRequest<{ url: string; sessionId: string }>(
      `/organisations/${this.organisationId}/cart/${cartId}/checkout${qs ? `?${qs}` : ''}`,
      { method: 'POST' },
    );
  }

  // ===========================================================================
  // Reviews
  // ===========================================================================

  async getProductReviews(
    channelId: string,
    handle: string,
  ): Promise<PaginatedResult<StorefrontReview>> {
    return this.storefrontRequest<PaginatedResult<StorefrontReview>>(
      `/storefront/${channelId}/products/${handle}/reviews`,
    );
  }

  // ===========================================================================
  // Recommendations
  // ===========================================================================

  async getRecommendations(
    channelId: string,
    handle: string,
  ): Promise<StorefrontProduct[]> {
    return this.storefrontRequest<StorefrontProduct[]>(
      `/storefront/${channelId}/products/${handle}/recommendations`,
    );
  }

  // ===========================================================================
  // Customer Orders
  // ===========================================================================

  async getCustomerOrders(
    params: { page?: number; limit?: number; status?: string } = {},
  ): Promise<PaginatedResult<CustomerOrder>> {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));
    if (params.status) search.set('status', params.status);
    const qs = search.toString();

    return this.storefrontRequest<PaginatedResult<CustomerOrder>>(
      `/organisations/${this.organisationId}/customer-commerce/orders${qs ? `?${qs}` : ''}`,
    );
  }
}

export function createCommerceClient(
  organisationId: string,
): CommerceAPIClient {
  return new CommerceAPIClient(organisationId);
}
